/**
 * 平台检测工具函数
 */

/**
 * 检测是否为 iOS 设备
 * @param {string} ua - UserAgent 字符串
 * @returns {boolean}
 */
export const isIOS = (ua) => {
  return /iPad|iPhone|iPod/.test(ua) || 
         (ua.includes('Mac') && 'ontouchend' in document)
}

/**
 * 检测是否为 iPad 设备
 * @param {string} ua - UserAgent 字符串
 * @returns {boolean}
 */
export const isIPad = (ua) => {
  if (!ua) {
    ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  }
  // iPad 检测：UserAgent 中包含 "iPad" 或者 (Mac + touch)
  return /iPad/.test(ua) || 
         (ua.includes('Mac') && 'ontouchend' in document && window.screen.width >= 768)
}

/**
 * 检测是否为鸿蒙 HarmonyOS 设备 (包含旧版和 NEXT)
 * @param {string} ua - UserAgent 字符串
 * @returns {boolean}
 */
export const isHarmonyOS = (ua) => {
  return /HarmonyOS/i.test(ua) || /OpenHarmony/i.test(ua)
}

/**
 * 检测是否为 HarmonyOS NEXT (5.0+)
 * 策略：
 * 1. 包含 OpenHarmony
 * 2. 包含 HarmonyOS 且版本号 >= 5
 * 3. 包含 HarmonyOS 且不包含 Android (某些纯鸿蒙可能不带 Android 兼容标识)
 * 
 * @param {string} ua - UserAgent 字符串
 * @returns {boolean}
 */
export const isHarmonyOSNext = (ua) => {
  // 检查 OpenHarmony (通常是 NEXT)
  if (/OpenHarmony/i.test(ua)) return true;

  // 检查 HarmonyOS 版本号
  const match = ua.match(/HarmonyOS\s+(\d+(?:\.\d+)*)/i);
  if (match) {
    const version = parseFloat(match[1]);
    if (version >= 5.0) return true;
  }
  
  // 如果是 HarmonyOS 但不包含 Android，也可能是 NEXT (保守策略)
  // 注意：早期的 HarmonyOS 也可能兼容 Android 但 UA 策略不同，这里主要依赖版本号和 OpenHarmony 标识
  return false;
}

/**
 * 检测是否为 Android 设备（排除鸿蒙）
 * @param {string} ua - UserAgent 字符串
 * @returns {boolean}
 */
export const isAndroid = (ua) => {
  // 如果是鸿蒙，需要进一步判断是否兼容 Android
  // 但为了业务逻辑简单，我们通常把鸿蒙单独处理
  if (isHarmonyOS(ua)) {
    return false
  }
  return /Android|Adr/i.test(ua)
}

/**
 * 检测是否为 Windows 设备
 * @param {string} ua - UserAgent 字符串
 * @returns {boolean}
 */
export const isWindows = (ua) => {
  return /Windows NT/i.test(ua)
}

/**
 * 检测是否为 macOS 设备（非移动端）
 * @param {string} ua - UserAgent 字符串
 * @returns {boolean}
 */
export const isMacOS = (ua) => {
  return /Macintosh|Mac OS X/i.test(ua) && !('ontouchend' in document)
}

/**
 * 获取当前平台类型
 * @returns {string} 'ios' | 'ipad' | 'android' | 'harmonyos' | 'harmonyos_next' | 'windows' | 'macos' | 'default'
 */
export const getPlatform = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'default'
  }
  
  const ua = navigator.userAgent
  
  if (isHarmonyOS(ua)) {
    return isHarmonyOSNext(ua) ? 'harmonyos_next' : 'harmonyos'
  }
  // 先检测 iPad，因为 iPad 也匹配 isIOS
  if (isIPad(ua)) return 'ipad'
  if (isIOS(ua)) return 'ios'
  if (isAndroid(ua)) return 'android'
  if (isWindows(ua)) return 'windows'
  if (isMacOS(ua)) return 'macos'
  
  return 'default'
}

/**
 * 检测是否为微信环境
 * @param {string} ua - UserAgent 字符串
 * @returns {boolean}
 */
export const isWeChat = (ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '')) => {
  return /MicroMessenger/i.test(ua)
}

// ==================== 欧那 APP 内嵌检测 ====================

/**
 * 检测是否在欧那 APP 内
 * 通过判断 User-Agent 是否包含 "OunaApp" 来识别
 * UA 格式: OunaApp/6.0.1 Platform/Android(OS/14) Net/wifi Env/release UserRole/student(id)
 * @returns {boolean} 是否在 APP 内
 */
export const isInOunaApp = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }
  const ua = navigator.userAgent || ''
  return ua.includes('OunaApp')
}
